// third party imports
import { useCallback, useMemo, useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Input,
  Select,
  Popover,
  message,
  Modal,
} from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { Edit2 } from "react-feather";
import dayjs from "dayjs";

// internal imports
import "./stylesPikkemBets.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";
import { formatDateNoTimezone } from "../../../../utils/dateService";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { AGENT_ACTIONS, KPI_CARD_BACKGROUND } from "../../../../utils/Constants";

// component imports
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import SettleBetPopup from "./SettleBetPopup/SettleBetPopup";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import BetCancelModal from "./BetCancelModal/BetCancelModal";
import CustomPagination from "../../../shared/CustomPagination/CustomPagination";
import DataTable from "../../../shared/dashboards/DataTable/DataTable";
import useExternalTableFilters from "../../../shared/table-filters/useExternalTableFilters";
import { toQueryString } from "../../../shared/table-filters/filterQueryUtils";
import AppContext from "../../../../context/AppContext";
import { formatCurrency as formatFiatCurrency } from "../../../../utils/financialFormatters";

const PikkemBets = ({ userData, _adminData, currencySymbol }) => {
  const navigate = useNavigate();
  const token = useMemo(() => JSON.parse(localStorage.getItem("token")), []);
  const PrivateAdminPanelInstance = useMemo(
    () => createPrivateAdminPanelInstance(token?.AccessToken),
    [token?.AccessToken],
  );
  
  const { adminActions } = useContext(AppContext);
  const canSettleBet = adminActions?.includes(AGENT_ACTIONS?.SETTLE_BET);

  // State variables
  const [, setBets] = useState([]);
  const betsRef = useRef([]);
  const [filteredBets, setFilteredBets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isKpisLoading, setIsKpisLoading] = useState(true); // Separate loading state for KPIs
  const lastEvaluatedKeyRef = useRef(null);
  const [_showExtraFilters, _setShowExtraFilters] = useState(false);
  const [, setBetsCache] = useState({});
  const betsCacheRef = useRef({});
  const [totalBets, setTotalBets] = useState(0);

  const {
    filters,
    normalizedFilters,
    setFilter,
    resetFilters,
  } = useExternalTableFilters({
    searchValue: "",
    selectedSport: null,
    selectedLeague: null,
    selectedType: [],
    selectedResult: [],
    selectedCurrencyType: [],
  });

  const [isSettlePopupVisible, setIsSettlePopupVisible] = useState(false);
  const [selectedBet, setSelectedBet] = useState(null);
  const [isCancelBetModalOpen, setIsCancelBetModalOpen] = useState(false);
  // Summary stats
  const [summary, setSummary] = useState({
    total_wagers: 0,
    total_wagers_pct_change: 0,
    net_result: 0,
    total_net_result_pct_change: 0,
    profit_loss: 0,
    total_margin: 0,
    total_margin_pct_change: 0,
    total_avg_bet: 0,
    total_avg_bet_pct_change: 0,
    total_bets_count: 0,
    total_bets_pct_change: 0,
  });
  // Cache totals from first page load to include in subsequent pagination requests
  const [, setCachedTotals] = useState(null);
  const cachedTotalsRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [, setIsSearchMode] = useState(false);

  // Date picker
  const [pikkemBetsDates, setPikkemBetsDates] = useState(null);

  const debounceRef = useRef(null);

  // Sport and League options
  const sportOptions = [
    { value: "basketball", label: "Basketball" },
    { value: "football", label: "Football" },
    { value: "soccer", label: "Soccer" },
    { value: "baseball", label: "Baseball" },
  ];

  const leagueOptions = [
    { value: "nba", label: "NBA" },
    { value: "nfl", label: "NFL" },
    { value: "mlb", label: "MLB" },
    { value: "premier_league", label: "Premier League" },
  ];

  // Filter options
  const typeOptions = [
    { value: "super_pick", label: "Super Pick" },
    { value: "safe_slip", label: "Safe Slip" },
  ];

  const resultOptions = [
    { value: "won", label: "Won" },
    { value: "lost", label: "Lost" },
    { value: "pending", label: "Pending" },
    { value: "void", label: "Void" },
  ];

  const currencyOptions = [
    { value: "USD", label: "USD" },
    { value: "free_bet", label: "Free Bet" },
  ];

  const handleMarket = useCallback((record) => {
    return record?.selection?.market?.market_name ?? "-";
  }, []);

  const handleSelection = useCallback((record) => {
    return record?.selection?.market?.outcome[0]?.name ?? "-";
  }, []);

  const getPlayers = useCallback((bets = []) => {
    return bets?.reduce((players, betData) => {
      const player = betData?.selection?.market?.player_name;
      return [...players, player ?? "-"];
    }, []);
  }, []);

  const handleSuperSafeMultiplier = useCallback((record) => {
    return Object.values(record?.pick_payout?.legs);
  }, []);

  // get bets cache key
  const getCacheKey = useCallback((page) => {
    const startDate = pikkemBetsDates?.[0]?.format("YYYY-MM-DD") || "all";
    const endDate = pikkemBetsDates?.[1]?.format("YYYY-MM-DD") || "all";
    return `${page}-${pageSize}-${startDate}-${endDate}`;
  }, [pageSize, pikkemBetsDates]);

  // clear bets cache
  const clearCache = useCallback(() => {
    betsCacheRef.current = {};
    cachedTotalsRef.current = null;
    setBetsCache({});
    setCachedTotals(null); // Clear cached totals when clearing cache
    setIsKpisLoading(true); // Show KPI loading when cache is cleared
  }, []);

  const fetchPikkemBets = useCallback(async (page = 1, refresh = false) => {
    if (!userData?.user_id) return;

    const cacheKey = getCacheKey(page);

    // if not refresh and bets cache has data, use the cache
    if (betsCacheRef.current[cacheKey] && !refresh) {
      const cachedData = betsCacheRef.current[cacheKey];
      betsRef.current = cachedData.bets;
      setBets(cachedData.bets);
      setFilteredBets(cachedData.bets);
      lastEvaluatedKeyRef.current = cachedData.lastEvaluatedKey;
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const params = {
        page,
        page_size: pageSize,
        start_date: pikkemBetsDates?.[0]?.format("YYYY-MM-DD"),
        end_date: pikkemBetsDates?.[1]?.format("YYYY-MM-DD"),
        last_evaluated_key: lastEvaluatedKeyRef.current,
      };

      // Include cached totals in subsequent page requests to prevent BE recalculation
      if (page > 1 && cachedTotalsRef.current && !refresh) {
        params.totals = JSON.stringify(cachedTotalsRef.current);
      }

      const queryString = toQueryString(params, {
        includeQuestionMark: false,
      });

      const response = await PrivateAdminPanelInstance?.get(
        API_ROUTES?.GET_USER_BETS?.replace("{user_id}", userData?.user_id) +
          `?type=pikkem${queryString ? `&${queryString}` : ""}`,
      );

      const responseData = response?.data?.data;
      const betsData = Array.isArray(responseData?.items)
        ? responseData?.items
        : [];

      const formattedBetsData = betsData.map((bet) => {
        return {
          ...bet,
          markets: bet?.bets?.map((betData) => handleMarket(betData)),
          players: getPlayers(bet?.bets),
          selections: bet?.bets?.map((betData) => handleSelection(betData)),
          first_place_multiplier: bet?.pick_payout?.first_place,
          final_multiplier: bet?.final_multiplier || "-",
          super_safe_multiplier: handleSuperSafeMultiplier(bet),
        };
      });

      lastEvaluatedKeyRef.current = responseData?.last_evaluated_key;
      betsRef.current = formattedBetsData;
      setBets(formattedBetsData);
      setFilteredBets(formattedBetsData);
      setTotalBets(responseData?.totals?.total_bets_count ?? 0);
      setIsSearchMode(false);

      // store in cache
      setBetsCache((prev) => {
        const nextCache = {
          ...prev,
          [cacheKey]: {
          bets: formattedBetsData,
          lastEvaluatedKey: responseData?.last_evaluated_key,
          },
        };
        betsCacheRef.current = nextCache;
        return nextCache;
      });

      // Only update summary/KPIs on first page load or when refresh is true (date filter change)
      // This prevents KPIs from being recalculated on every page
      const totalsFromResponse = responseData?.totals;
      if ((page === 1 || refresh) && totalsFromResponse) {
        setSummary({
          total_wagers: totalsFromResponse?.total_wagers || 0,
          total_wagers_pct_change:
            totalsFromResponse?.total_wagers_pct_change || null,
          net_result: totalsFromResponse?.net_result || 0,
          total_net_result_pct_change:
            totalsFromResponse?.total_net_result_pct_change || null,
          total_margin: totalsFromResponse?.total_margin || 0,
          total_margin_pct_change:
            totalsFromResponse?.total_margin_pct_change || null,
          total_avg_bet: totalsFromResponse?.total_avg_bet || 0,
          total_avg_bet_pct_change:
            totalsFromResponse?.total_avg_bet_pct_change || null,
          total_bets_count:
            totalsFromResponse?.total_bets_count || betsData.length,
          total_bets_count_pct_change:
            totalsFromResponse?.total_bets_count_pct_change || null,
        });
        // Cache totals for subsequent pagination requests
        cachedTotalsRef.current = totalsFromResponse;
        setCachedTotals(totalsFromResponse);
        // KPIs finished loading
        setIsKpisLoading(false);
      }
    } catch (error) {
      console.error("Error fetching Pikkem bets:", error);
      handleExpiredTokenError(navigate, error?.response);

      // Set mock data for development/demo
      // const mockBets = generateMockBets();
      // setBets(mockBets);
      // setFilteredBets(mockBets);
    } finally {
      setIsLoading(false);
    }
  }, [
    PrivateAdminPanelInstance,
    getCacheKey,
    getPlayers,
    handleMarket,
    handleSelection,
    handleSuperSafeMultiplier,
    navigate,
    pageSize,
    pikkemBetsDates,
    userData?.user_id,
  ]);

  const fetchPikkemBetsRef = useRef(fetchPikkemBets);

  useEffect(() => {
    fetchPikkemBetsRef.current = fetchPikkemBets;
  }, [fetchPikkemBets]);

  const _generateMockBets = () => {
    const mockData = [];
    for (let i = 0; i < 10; i++) {
      mockData.push({
        key: i + 1,
        bet_id: `#2232992${i}`,
        created_at: "25/5/24 12:34 PM",
        markets: ["Points Over", "Rebounds Under", "First Goal"],
        selections: [
          { name: "Z. Edey o20", odds: "" },
          { name: "N. Queta u8", odds: "o1" },
        ],
        currency: i % 3 === 0 ? "Free Bet" : "USD",
        wager_amount: 100000.0,
        finalized_payout: 1000000.0,
        final_multiplier: "10x",
        status: ["Won", "Lost", "Pending", "Void"][i % 4],
        type: i % 2 === 0 ? "Super Pick" : "Safe Slip",
        potential_odds: i % 2 === 0 ? "+660/0000" : "+650",
        balance: 1000000.0,
        legs: [
          {
            market: "Points Over",
            selection: "Z. Edey o20",
            status: "Unsettled",
          },
          {
            market: "Rebounds Under",
            selection: "N. Queta u20",
            status: "Unsettled",
          },
          {
            market: "First Goal",
            selection: "L. Messi o1",
            status: "Unsettled",
          },
        ],
      });
    }
    return mockData;
  };

  const searchByPickId = useCallback(async (pick_id) => {
    try {
      setIsLoading(true);
      const response = await PrivateAdminPanelInstance?.get(
        API_ROUTES?.GET_BET_BY_ID?.replace("{user_id}", userData?.user_id)?.replace("{bet_id}", pick_id)?.replace(
          "{bet_type}",
          "5",
        ),
      );
      const responseData = response?.data?.data;

      const formattedBetsData = [responseData].map((bet) => {
        return {
          ...bet,
          markets: bet?.bets?.map((betData) => handleMarket(betData)),
          players: getPlayers(bet?.bets),
          selections: bet?.bets?.map((betData) => handleSelection(betData)),
          first_place_multiplier: bet?.pick_payout?.first_place,
          final_multiplier: bet?.final_multiplier || "-",
        };
      });
      betsRef.current = formattedBetsData;
      setBets(formattedBetsData);
      setFilteredBets(formattedBetsData);
      setIsSearchMode(true);
    } catch (error) {
      console.error("Error searching by pick id", error);
      message.error(error?.response?.data?.message || AppStrings.ERROR_SEARCHING_BETS);
    } finally {
      setIsLoading(false);
    }
  }, [
    PrivateAdminPanelInstance,
    getPlayers,
    handleMarket,
    handleSelection,
    userData?.user_id,
  ]);

  const applyFilters = useCallback(() => {
    let filtered = [...betsRef.current];

    // Search filter
    if (normalizedFilters.searchValue) {
      searchByPickId(normalizedFilters.searchValue);
      return;
    }

    // Type filter
    if (filters.selectedType.length > 0) {
      filtered = filtered.filter((bet) => {
        const betType = bet.type?.toLowerCase().replace(" ", "_");
        return filters.selectedType.includes(betType);
      });
    }

    // Result filter
    if (filters.selectedResult.length > 0) {
      filtered = filtered.filter((bet) =>
        filters.selectedResult.includes(bet.status?.toLowerCase()),
      );
    }

    // Currency filter
    if (filters.selectedCurrencyType.length > 0) {
      filtered = filtered.filter((bet) => {
        if (filters.selectedCurrencyType.includes("free_bet")) {
          return bet.currency?.toLowerCase() === "free bet";
        }
        return filters.selectedCurrencyType.includes(bet.currency?.toUpperCase());
      });
    }

    setFilteredBets(filtered);
  }, [
    filters.selectedCurrencyType,
    filters.selectedResult,
    filters.selectedType,
    normalizedFilters.searchValue,
    searchByPickId,
  ]);

  const applyFiltersRef = useRef(applyFilters);

  useEffect(() => {
    applyFiltersRef.current = applyFilters;
  }, [applyFilters]);

  // Load bets on mount
  useEffect(() => {
    if (userData?.user_id) {
      fetchPikkemBetsRef.current(1, true);
    }
  }, [userData?.user_id]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      applyFiltersRef.current();
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [filters.searchValue]);

  const handleClearFilters = () => {
    resetFilters();
    setCurrentPage(1);
    setIsSearchMode(false);
  };

  const handleApply = () => {
    setCurrentPage(1);
    applyFilters();
  };

  const handleEditBet = (bet) => {
    setSelectedBet(bet);
    setIsSettlePopupVisible(true);
  };

  const handleCancelBet = (bet) => {
    setSelectedBet(bet);
    setIsCancelBetModalOpen(true);
  };

  const getStatusClass = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "won":
        return "status-won";
      case "lost":
        return "status-lost";
      case "pending":
        return "status-pending";
      case "cancelled":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const formatBetMoney = (value, moneyType) => {
    const normalizedMoneyType = String(moneyType ?? "").toLowerCase();
    if (normalizedMoneyType === "usd" || normalizedMoneyType === "cash") {
      return formatFiatCurrency(Number(value ?? 0), currencySymbol, 2);
    }
    return Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value ?? 0));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success(AppStrings.PAM_COPIED_TO_CLIPBOARD);
  };

  const handlePayout = (record) => {
    if (record?.display_status === "Cancelled") {
      return formatBetMoney(record?.wager_amount, record?.money_type);
    }
    if (record?.display_status === "Lost") {
      return formatBetMoney(0, record?.money_type);
    }
    return record?.payout !== null
      ? formatBetMoney(record?.payout, record?.money_type)
      : "-";
  };

  const columns = [
    {
      title: "Date&Time",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (text) =>
        text ? formatDateNoTimezone(text, "DD MMMM YYYY, HH:mm:ss") : "-",
    },
    {
      title: "Arena ID",
      dataIndex: "arena_id",
      key: "arena_id",
      width: 100,
      render: (text) => (
        <Popover content={text} placement="topLeft">
          <span
            className="pikkem-bet-id-text"
            onClick={() => copyToClipboard(text)}
          >
            {text || "-"}
          </span>
        </Popover>
      ),
    },
    {
      title: "Pick ID",
      dataIndex: "bet_id",
      key: "bet_id",
      width: 100,
      render: (text) => (
        <Popover content={text} placement="topLeft">
          <span
            className="pikkem-bet-id-text"
            onClick={() => copyToClipboard(text || "-")}
          >
            {text || "-"}
          </span>
        </Popover>
      ),
    },
    {
      title: "Player",
      dataIndex: "players",
      key: "players",
      width: 150,
      render: (players) => {
        if (!players?.length) {
          return "-";
        }
        return (
          <div className="pikkem-selections-cell">
            {players.map((player, idx) => (
              <div key={idx} className="pikkem-selection-item">
                {player}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: "Markets",
      dataIndex: "markets",
      key: "markets",
      width: 150,
      render: (markets) => (
        <div className="pikkem-markets-cell">
          {markets?.map((market, idx) => (
            <div key={idx} className="pikkem-market-item">
              {market}
            </div>
          )) || "-"}
        </div>
      ),
    },
    {
      title: "Selections",
      dataIndex: "selections",
      key: "selections",
      width: 100,
      render: (selections) => (
        <div className="pikkem-selections-cell">
          {selections?.map((sel, idx) => (
            <div key={idx} className="pikkem-selection-item">
              {sel || "-"}
            </div>
          )) || "-"}
        </div>
      ),
    },
    {
      title: "Super/Safe Mult.",
      dataIndex: "super_safe_multiplier",
      key: "super_safe_multiplier",
      width: 150,
      render: (super_safe_multiplier) => (
        <div className="pikkem-super-safe-multiplier-cell">
          {super_safe_multiplier?.map((multiplier, idx) => (
            <div key={idx} className="pikkem-super-safe-multiplier-item">
              {multiplier + "x" || "-"}
            </div>
          )) || "-"}
        </div>
      ),
    },
    {
      title: "1st Place Mult.",
      dataIndex: "first_place_multiplier",
      key: "first_place_multiplier",
      width: 150,
      render: (value) => (value ? value + "x" : "-"),
    },
    {
      title: "Currency",
      dataIndex: "money_type",
      key: "money_type",
      width: 100,
    },
    {
      title: "Wager",
      dataIndex: "wager_amount",
      key: "wager_amount",
      sorter: (a, b) => a.wager_amount - b.wager_amount,
      width: 100,
      render: (value, record) =>
        value ? formatBetMoney(value, record?.money_type) : "-",
    },
    {
      title: "Payout",
      dataIndex: "payout",
      key: "payout",
      sorter: (a, b) => a.payout - b.payout,
      width: 100,
      render: (value, record) => handlePayout(record),
    },
    {
      title: "Final Mult.",
      dataIndex: "final_multiplier",
      key: "final_multiplier",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "display_status",
      key: "display_status",
      width: 100,
      render: (display_status) => (
        <span
          className={`pikkem-status-badge ${getStatusClass(display_status)}`}
        >
          {display_status || "-"}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "pick_type",
      key: "pick_type",
      width: 100,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      sorter: true,
      showSorterTooltip: false,
      width: 130,
      render: (value, record) =>
        value ? formatBetMoney(value, record?.money_type) : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <div className="actions-cell">
          <Popover content="Edit Bet" placement="topLeft">
            <button
              className={`pikkem-edit-bet-button ${
                record?.finalized === "1" && canSettleBet ? "" : "disabled"
              }`}
              onClick={() => handleEditBet(record)}
              disabled={record?.finalized === "1" && canSettleBet ? false : true}
            >
              <Edit2 size={16} />
            </button>
          </Popover>
          <Popover content="Cancel Bet" placement="topLeft">
            <button
              className={`pikkem-close-bet-button ${
                record?.finalized === "1" && canSettleBet ? "" : "disabled"
              }`}
              onClick={() => handleCancelBet(record)}
              disabled={record?.finalized === "1" && canSettleBet ? false : true}
            >
              <CloseOutlined />
            </button>
          </Popover>
          {/* <button
            className="pikkem-edit-bet-button"
            onClick={() => handleViewPikkemBet(record)}
          >
            <img src={ArenaIcon} alt="arena icon" />
          </button> */}
        </div>
      ),
    },
  ];

  const refreshBets = () => {
    cachedTotalsRef.current = null;
    setCachedTotals(null); // Clear cached totals on refresh
    setIsKpisLoading(true); // Show KPI loading on refresh
    fetchPikkemBets(1, true);
  };

  // when dates change, clear cache and fetch from page 1
  useEffect(() => {
    clearCache();
    setCurrentPage(1);
    fetchPikkemBetsRef.current(1, true);
  }, [clearCache, pikkemBetsDates]);

  // when page changes, fetch data (using cache if available)
  useEffect(() => {
    fetchPikkemBetsRef.current(currentPage, false);
  }, [currentPage, pageSize]);

  return (
    <div className="pikkem-bets-container">
      <Modal
        closable={false}
        open={isCancelBetModalOpen}
        onCancel={() => setIsCancelBetModalOpen(false)}
        footer={null}
      >
        <BetCancelModal
          bet={selectedBet}
          refreshBets={refreshBets}
          handleCancel={() => setIsCancelBetModalOpen(false)}
        />
      </Modal>
      {/* Top Filters - Sport and League */}
      <div className="pikkem-top-filters" style={{ display: "none" }}>
        <div className="pikkem-top-filter-row">
          <div className="pikkem-top-filter-item">
            <span className="pikkem-filter-label">Sport</span>
            <Select
              placeholder={AppStrings.NOT_SELECTED}
              value={filters.selectedSport}
              onChange={(value) => setFilter("selectedSport", value)}
              allowClear
              className="pikkem-top-select"
              options={sportOptions}
            />
          </div>
          <div className="pikkem-top-filter-item">
            <span className="pikkem-filter-label">League</span>
            <Select
              placeholder={AppStrings.NOT_SELECTED}
              value={filters.selectedLeague}
              onChange={(value) => setFilter("selectedLeague", value)}
              allowClear
              className="pikkem-top-select"
              options={leagueOptions}
            />
          </div>
        </div>
        <div className="pikkem-top-filter-actions">
          <button className="pikkem-clear-btn" onClick={handleClearFilters}>
            {AppStrings.CLEAR_FILTERS}
          </button>
          <button className="pikkem-apply-btn" onClick={handleApply}>
            {AppStrings.APPLY}
          </button>
        </div>
      </div>
      <div className="pikkem-date-picker-container">
        <CustomDatePicker
          width={300}
          getData={(queryString) => {
            if (queryString) {
              const params = new URLSearchParams(queryString);
              const startDate = params.get("start_date");
              const endDate = params.get("end_date");
              if (startDate && endDate) {
                setPikkemBetsDates([dayjs(startDate), dayjs(endDate)]);
              } else {
                setPikkemBetsDates(null);
              }
            } else {
              setPikkemBetsDates(null);
            }
          }}
          dates={pikkemBetsDates}
          setDates={setPikkemBetsDates}
          showQuickSelect={true}
          showPresets={true}
          allowClearToNull={true}
        />
      </div>
      {/* Summary Cards */}
      <div className="pikkem-kpi-cards">
        <div className="pikkem-kpi-cards-left-content">
          <div className="pikkem-kpi-row">
            <div className="pikkem-kpi-item">
              <KpiCard
                title={AppStrings.TURNOVER}
                value={summary.total_wagers}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpisLoading}
                percentage={summary.total_wagers_pct_change}
                percentageLabel={AppStrings.VS_LAST_MONTH}
                currencySymbol={currencySymbol}
              />
            </div>
            <div className="pikkem-kpi-item">
              <KpiCard
                title={AppStrings.PROFIT_LOSS}
                value={summary.net_result}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpisLoading}
                percentage={summary.total_net_result_pct_change}
                percentageLabel={AppStrings.VS_LAST_MONTH}
                currencySymbol={currencySymbol}
              />
            </div>
            <div className="pikkem-kpi-item">
              <KpiCard
                title={AppStrings.MARGIN}
                value={summary.total_margin}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpisLoading}
                rate={true}
                percentage={summary.total_margin_pct_change}
                percentageLabel={AppStrings.VS_LAST_MONTH}
                isPercentageValue={true}
              />
            </div>
            <div className="pikkem-kpi-item">
              <KpiCard
                title={AppStrings.AVG_BET_AMOUNT}
                value={summary.total_avg_bet}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpisLoading}
                percentage={summary.total_avg_bet_pct_change}
                percentageLabel={AppStrings.VS_LAST_MONTH}
                currencySymbol={currencySymbol}
              />
            </div>
            <div className="pikkem-kpi-item">
              <KpiCard
                title={AppStrings.TOTAL_BETS}
                value={summary.total_bets_count}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpisLoading}
                percentage={summary.total_bets_count_pct_change}
                percentageLabel={AppStrings.VS_LAST_MONTH}
                maximumFractionDigits={0}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Bets Section */}
      <div className="pikkem-table-section">
        {/* Search and Controls */}
        <div className="pikkem-content-header">
          <h3 className="pikkem-section-title">{AppStrings.PIKKEM_BETS}</h3>
          <div className="pikkem-autocomplete-container">
            <div className="pikkem-search-wrapper">
              <Input
                placeholder="Search"
                prefix={
                  <SearchOutlined
                    style={{
                      fontSize: "18px",
                      color: "var(--COLOR-LIGHT_TEXT_COLOR)",
                    }}
                  />
                }
                value={filters.searchValue}
                onChange={(e) => {
                  setFilter("searchValue", e.target.value);
                  if (!e.target.value) {
                    setIsSearchMode(false);
                    fetchPikkemBets(1, true);
                  }
                }}
                className="pikkem-search-input"
                allowClear
              />
            </div>
            {/* <div className="pikkem-divider"></div> */}
            {/* <button className="pikkem-date-btn">
              <Calendar size={16} />
              <span>Last 7 days</span>
            </button> */}

            {/* <button className="pikkem-download-btn">
              <Download size={16} />
            </button> */}
          </div>
        </div>

        {/* Table Filters */}
        <div className="pikkem-table-filters" style={{ display: "none" }}>
          <div className="pikkem-filter-item">
            <span className="pikkem-filter-label">Type</span>
            <Select
              mode="multiple"
              placeholder={AppStrings.NOT_SELECTED}
              value={filters.selectedType}
              onChange={(value) => setFilter("selectedType", value)}
              options={typeOptions}
              className="pikkem-filter-select"
            />
          </div>
          <div className="pikkem-filter-item">
            <span className="pikkem-filter-label">Result</span>
            <Select
              mode="multiple"
              placeholder={AppStrings.NOT_SELECTED}
              value={filters.selectedResult}
              onChange={(value) => setFilter("selectedResult", value)}
              options={resultOptions}
              className="pikkem-filter-select"
            />
          </div>
          <div className="pikkem-filter-item">
            <span className="pikkem-filter-label">Currency Type</span>
            <Select
              mode="multiple"
              placeholder={AppStrings.NOT_SELECTED}
              value={filters.selectedCurrencyType}
              onChange={(value) => setFilter("selectedCurrencyType", value)}
              options={currencyOptions}
              className="pikkem-filter-select"
            />
          </div>
        </div>

        {/* Table */}
        <div className="pikkem-table-container">
          {isLoading ? (
            <div className="pikkem-loading-container">
              <RebetLoader height="100px" width="100px" />
            </div>
          ) : (
            <DataTable
              dataSource={filteredBets}
              columns={columns}
              rowKey={(record) => record?.bet_id || record?.arena_id}
              pagination={false}
              scrollPreset="default"
              scrollOverrides={{ x: 1400 }}
              className="pikkem-bets-table"
            />
          )}
        </div>

        {/* Pagination */}
        {!isLoading && filteredBets.length > 0 && (
          <div className="pikkem-pagination-container">
              <CustomPagination
                total={totalBets}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageSizeChange={(value) => {
                  clearCache();
                  setCurrentPage(1);
                  setPageSize(value);
                }}
                onPageChange={(page) => setCurrentPage(page)}
                itemLabel="bets"
                pageSizeOptions={[10, 20, 50, 100]}
                restrictToAdjacentPages
              />
            </div>
        )}
      </div>

      {/* Settle Bet Popup */}
      <SettleBetPopup
        isVisible={isSettlePopupVisible}
        onClose={() => setIsSettlePopupVisible(false)}
        betData={selectedBet}
        refreshBets={refreshBets}
      />
    </div>
  );
};

export default PikkemBets;
